import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Users, Award, CheckCircle, Shield, FileText, DollarSign, Calendar, Stethoscope, Siren, Cpu, Heart, HeartPulse, Droplet, Ambulance, ShieldCheck, GraduationCap, X, ArrowRight, Lock, Truck, Calculator, Leaf, Sparkles, Flower2, Sun, Brain } from 'lucide-react';
import Programs3DCube from '../components/Programs3DCube';
import Seo from '../components/Seo';

// Program catalog powering the compact tile grid + bio modal on the landing page.
// All programs are delivered 100% online, self-paced. Flat pricing everywhere:
// $99 course fee · $99 midterm exam · $99 final exam. No grants. No in-person classes.
const PROGRAMS = [
  {
    code: 'ONLINE_CNA_PREP',
    courseId: 'cna-prep',
    title: 'Certified Medical Assistant (CMA) Training Program — Online',
    short: 'Healthcare entry point, fully online',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Heart,
    accent: '#1e3a5f',
    bg: '#eff6ff',
    badge: 'ONLINE',
    bio: 'Become a Certified Medical Assistant through a fully online, self-paced program. Covers patient care, HIPAA & ethics, infection control, vital signs, mobility, nutrition, end-of-life care, and CMA proficiency exam preparation. Earn a certificate of completion after passing all module quizzes.',
    features: [
      '10 self-paced reading modules + 50 quiz questions (no videos)',
      '$99 program enrollment · $2,401 deferred · $2,500 stated tuition',
      '$99 mid-term &amp; $99 final exams · Certificate of completion + transcript',
      'In-person Skills Lab + Clinical OJT required for licensure readiness',
    ],
    outcomes: 'Nursing Assistant · Patient Care Technician · Home Health Aide · Long-Term Care Assistant',
  },
  {
    code: 'ONLINE_LPN_PREP',
    courseId: 'lpn-prep',
    title: 'Practical Nurse (PN) — Online',
    short: 'Online RTI + 600 supervised clinical hours at a site you choose',
    duration: 'Self-Paced — Online + Clinicals',
    tuition: '$99',
    icon: Stethoscope,
    accent: '#0d9488',
    bg: '#ecfdf5',
    badge: 'HYBRID',
    bio: 'Hybrid Practical Nurse (PN) program — all theory delivered online (patient assessment, medication administration, clinical procedures, infection control, therapeutic communication, documentation, nursing ethics, NCLEX-PN preparation). Students complete 600 supervised clinical hours at a hospital, long-term-care facility, or clinic of their own choice; MedCare provides a printable Clinical Affiliation Form to take to the site. Sixteen reading modules with quizzes — the full PN training manual stays open beside you the whole way through.',
    features: [
      '16 reading modules + 80 quiz questions (no videos)',
      '$99 program enrollment · $8,401 deferred · $8,500 stated tuition',
      '$99 mid-term &amp; $99 final exams · Downloadable Clinical Affiliation Form',
      '600 supervised clinical hours required for NCLEX-PN-readiness Certificate',
    ],
    outcomes: 'Practical-Nurse Trainee · Medication Aide · NCLEX-PN Ready',
  },
  {
    code: 'ONLINE_PHLEBOTOMY_PREP',
    courseId: 'phlebotomy-prep',
    title: 'Phlebotomy Training — Online',
    short: 'Blood collection & laboratory procedures',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Droplet,
    accent: '#b45309',
    bg: '#fef3c7',
    badge: 'ONLINE',
    bio: 'Master blood-collection techniques and laboratory procedures entirely online. Learn venipuncture and capillary puncture theory, tube handling, specimen processing, infection control, and patient interaction. Includes mid-term and final exam plus a downloadable training manual.',
    features: [
      '6 self-paced reading modules + 30 quiz questions (no videos)',
      '$99 program enrollment · $2,401 deferred · $2,500 stated tuition',
      'Venipuncture theory & best practice',
      'In-person Skills Lab required for live-draw competency',
    ],
    outcomes: 'Phlebotomy Technician · Lab Assistant · Donor-Center Specialist',
  },
  {
    code: 'ONLINE_EMT_PREP',
    courseId: 'emt-prep',
    title: 'Emergency Medical Technician (EMT) Training — Online',
    short: 'Field emergency-response fundamentals',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Ambulance,
    accent: '#b91c1c',
    bg: '#fee2e2',
    badge: 'ONLINE',
    bio: 'Online EMT preparatory course covering patient assessment and triage, airway management, CPR/AED fundamentals, trauma basics, medical emergencies, pharmacology overview, and NREMT cognitive-exam preparation. Includes mid-term and final exam.',
    features: [
      '6 self-paced reading modules + 30 quiz questions (no videos)',
      '$99 program enrollment · $2,401 deferred · $2,500 stated tuition',
      'NREMT cognitive-exam preparation',
      'In-person Skills Lab + supervised field OJT required for NREMT readiness',
    ],
    outcomes: 'EMT Cognitive-Exam Ready · 911 Dispatch Support · Medical Transport Aide',
  },
  {
    code: 'ONLINE_HOMELAND_PREP',
    courseId: 'homeland-security-prep',
    title: 'Master Homeland Security Specialist — Online',
    short: '(4) FEMA ICS Trainings · IS-100 · IS-200 · IS-700 · IS-800',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: ShieldCheck,
    accent: '#0b2545',
    bg: '#0b2545',
    badge: 'ONLINE',
    dark: true,
    bio: 'Prepares students for civilian careers in emergency management, disaster response, healthcare emergency preparedness, and multi-agency coordination. Aligned with FEMA ICS/NIMS frameworks and includes the four core FEMA ICS prerequisite courses. Professional training only — does not confer law-enforcement certification, policing authority, or federal security clearance.',
    features: [
      '8 reading modules + 40 quiz questions (no videos)',
      '(4) FEMA ICS Trainings included: IS-100.c, IS-200.c, IS-700.b, IS-800.d',
      '$99 program enrollment · $2,401 deferred · $2,500 stated tuition',
      'Healthcare emergency response unit + capstone disaster-simulation reading module',
    ],
    outcomes: 'Emergency Management Technician · Disaster Response Specialist · Healthcare Emergency Preparedness Coordinator',
  },
  {
    code: 'ONLINE_MED_TERM_PREP',
    courseId: 'medical-terminology-prep',
    title: 'Medical Terminology — Online',
    short: 'Foundation for every healthcare role',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: BookOpen,
    accent: '#2563eb',
    bg: '#dbeafe',
    badge: 'ONLINE',
    bio: 'A compact medical terminology course designed for CMAs, medical assistants, phlebotomists, EMTs, and administrative healthcare staff. Learn prefixes, roots, suffixes, body-system vocabulary, and common clinical abbreviations. Quizzes after every module plus a mid-term and final exam.',
    features: [
      '6 self-paced reading modules + 30 quiz questions (no videos)',
      '$99 program enrollment · $2,401 deferred · $2,500 stated tuition',
      'Body-system vocabulary walk-through',
      'Certificate of completion',
    ],
    outcomes: 'Pre-requisite for CMA · MA · Phlebotomy · EMT roles',
  },
  {
    code: 'ONLINE_MEDICAL_AIDE_PREP',
    courseId: 'medical-aide-prep',
    title: 'Medical Aide — Outpatient Clinic Apprenticeship',
    short: 'Outpatient clinical & administrative skills',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Stethoscope,
    accent: '#2563eb',
    bg: '#dbeafe',
    badge: 'ONLINE',
    bio: 'Train as a Medical Aide for outpatient clinics, urgent care, and primary-care offices. Covers scope of practice, anatomy & medical terminology, vital signs & patient intake, infection control & PPE, clinical procedures, specimen collection, ECG placement, medication assistance, ethics, HIPAA, patient communication, and employability skills. Eight modules with quizzes plus paid mid-term and final exams ($99 each).',
    features: [
      '8 reading modules + 40 quiz questions (no videos)',
      '$99 program enrollment · $2,401 deferred · $2,500 stated tuition',
      '$99 mid-term & $99 final exams · Certificate of completion + transcript',
      'Practice settings: primary care, urgent care, OB/GYN, pediatrics',
    ],
    outcomes: 'Medical Aide · Clinical Assistant · Outpatient Care Tech · EHR Support',
  },
  {
    code: 'ONLINE_GED_PREP',
    courseId: 'ged-prep',
    title: 'GED Adult Learning — Online',
    short: 'Earn your GED credential fully online',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: GraduationCap,
    accent: '#7c3aed',
    bg: '#f3e8ff',
    badge: 'ONLINE',
    bio: 'Comprehensive GED preparatory program covering all four subject areas: Mathematical Reasoning, Reasoning Through Language Arts, Science, and Social Studies. Self-paced modules, 100-question quiz bank, mid-term and final exam. Earn an official landscape GED credential with Tennessee state seal on completion.',
    features: [
      '10 subject reading modules · 100 quiz questions (no videos)',
      'Single flat $99 — no mid-term or final-exam fee · no deferred tuition',
      'Official landscape GED credential + transcript',
      'Apprenticeship-stamped diploma',
    ],
    outcomes: 'High-School Equivalency · College-Ready · Workforce-Ready',
  },
  // ===================================================================
  // Feb 2026 — new program tiles (Cyber Security, Forklift OSHA,
  // Medical Billing & Coding, and the 5 Doctorate programs).
  // ===================================================================
  {
    code: 'ONLINE_CYBERSECURITY_PREP',
    courseId: 'cybersecurity-prep',
    title: 'Cyber Security — Online',
    short: 'SOC-ready foundations: defend, detect, respond',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Lock,
    accent: '#0ea5e9',
    bg: '#e0f2fe',
    badge: 'ONLINE',
    bio: 'A self-paced introduction to modern cyber security. Covers core security principles (CIA triad), network defense, identity & access management, endpoint protection, common attack techniques (phishing, ransomware, social engineering), basic incident response, and compliance frameworks (NIST CSF, HIPAA, PCI-DSS). Designed for career-changers entering the SOC-analyst / IT-security pipeline.',
    features: [
      '4 reading modules · 20+ quiz questions (no videos)',
      '$99 one-time enrollment — no mid-term or final-exam fee',
      'Certificate of completion + transcript',
      'Career path: SOC Analyst · Help-Desk Security · IT Compliance',
    ],
    outcomes: 'SOC Analyst · IT Security · Compliance Coordinator',
  },
  {
    code: 'ONLINE_FORKLIFT_OSHA',
    courseId: 'forklift-osha-prep',
    title: 'Forklift OSHA Certification — Online',
    short: 'OSHA-aligned formal-instruction module for 29 CFR 1910.178',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Truck,
    accent: '#ea580c',
    bg: '#ffedd5',
    badge: 'ONLINE',
    bio: 'Self-paced OSHA-aligned forklift operator training meeting 29 CFR 1910.178 formal-instruction requirements. Covers truck types, the stability triangle, pre-shift inspection, safe load handling, pedestrian safety, refueling/charging, and certification requirements. Employers must still complete the practical hands-on evaluation on the equipment the operator will actually use.',
    features: [
      '3 modules · 15+ quiz questions (no videos)',
      '$99 one-time enrollment — no mid-term or final-exam fee',
      'Certificate of completion + transcript',
      'Satisfies OSHA formal-instruction portion of certification',
    ],
    outcomes: 'Forklift Operator · Warehouse Associate · Material Handler',
  },
  {
    code: 'ONLINE_MEDICAL_BILLING_CODING',
    courseId: 'medical-billing-coding-prep',
    title: 'Medical Billing & Coding — Online',
    short: 'CPT · HCPCS · ICD-10 · revenue cycle · CPC/CCA prep',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Calculator,
    accent: '#0d9488',
    bg: '#ccfbf1',
    badge: 'ONLINE',
    bio: 'Self-paced online course preparing students for entry-level medical biller / coder roles. Covers CPT, HCPCS Level II, ICD-10-CM, and ICD-10-PCS code sets; the revenue-cycle lifecycle; clean-claim submission; HIPAA & PHI handling; payer types (commercial, Medicare, Medicaid); denial management; and prep for CPC / CCA certification exams.',
    features: [
      '4 reading modules · 20+ quiz questions (no videos)',
      '$99 enrollment · $99 mid-term · $99 final exam',
      'Certificate of completion + transcript',
      'Career path: Medical Coder · Billing Specialist · Claims Examiner',
    ],
    outcomes: 'Medical Coder · Billing Specialist · Claims Examiner · CPC/CCA Candidate',
  },
  {
    code: 'ONLINE_DOCTOR_NATURAL_HEALTH',
    courseId: 'doctor-natural-health',
    title: 'Doctor of Natural Health — Online',
    short: 'Doctorate-level non-licensed natural-health credential',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Leaf,
    accent: '#15803d',
    bg: '#dcfce7',
    badge: 'DOCTORATE',
    bio: 'Self-paced doctorate-level program in natural health for practitioners pursuing non-licensed wellness consulting careers. Covers foundations of naturopathy, nutritional science, botanical medicine, lifestyle medicine, mind-body modalities, ethics, scope of practice, and consultative practice-building. NOT a state-licensed medical degree.',
    features: [
      '4 reading modules · NCLEX-style quiz bank',
      '$99 enrollment · $99 mid-term · $199 final exam',
      'Doctorate certificate of completion + transcript',
      'Clear scope-of-practice disclosure: non-licensed wellness credential',
    ],
    outcomes: 'Natural-Health Consultant · Wellness Practitioner · Lifestyle Coach',
  },
  {
    code: 'ONLINE_DOCTOR_HOLISTIC_WELLNESS',
    courseId: 'doctor-holistic-wellness',
    title: 'Doctor of Holistic Wellness — Online',
    short: 'Eight-dimension holistic wellness consulting',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Sparkles,
    accent: '#9333ea',
    bg: '#f3e8ff',
    badge: 'DOCTORATE',
    bio: 'Self-paced doctorate-level program in holistic wellness coaching. Covers integrative health models, energy systems, mind-body practices, nutrition, stress and trauma-informed care, ethics, and scope of practice. Prepares graduates to serve as non-licensed holistic-wellness practitioners.',
    features: [
      '4 reading modules · NCLEX-style quiz bank',
      '$99 enrollment · $99 mid-term · $199 final exam',
      'Doctorate certificate of completion + transcript',
      'Non-licensed wellness credential — not a state-licensed medical degree',
    ],
    outcomes: 'Holistic Wellness Coach · Lifestyle Practitioner · Group Facilitator',
  },
  {
    code: 'ONLINE_HERBAL_HEALTH_PRACTITIONER',
    courseId: 'herbal-health-practitioner',
    title: 'Herbal Health Practitioner Certification — Online',
    short: 'Materia medica · preparations · safety · ethics',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Flower2,
    accent: '#16a34a',
    bg: '#dcfce7',
    badge: 'DOCTORATE',
    bio: 'Self-paced certification for non-licensed herbal-health practitioners. Covers plant identification, materia medica, preparation methods (teas, tinctures, salves), constitutional assessment, herb–drug interactions, ethics, and sustainable wildcrafting. Non-licensed credential — not a state-licensed medical or pharmacy degree.',
    features: [
      '4 reading modules · NCLEX-style quiz bank',
      '$99 enrollment · $99 mid-term · $199 final exam',
      'Doctorate certificate of completion + transcript',
      'Strong emphasis on herb–drug interactions and ethical practice',
    ],
    outcomes: 'Herbal Practitioner · Wellness Consultant · Botanical Educator',
  },
  {
    code: 'ONLINE_INTEGRATIVE_WELLNESS_CONSULTANT',
    courseId: 'integrative-wellness-consultant',
    title: 'Integrative Wellness Consultant — Online',
    short: 'Motivational interviewing · lifestyle pillars · group work',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Sun,
    accent: '#d97706',
    bg: '#fef3c7',
    badge: 'DOCTORATE',
    bio: 'Self-paced doctorate-level program preparing integrative wellness consultants to coach across nutrition, movement, mind-body, and lifestyle in alignment with conventional care teams. Covers consultative models, motivational interviewing, behavior change, group facilitation, ethics, and scope.',
    features: [
      '4 reading modules · NCLEX-style quiz bank',
      '$99 enrollment · $99 mid-term · $199 final exam',
      'Doctorate certificate of completion + transcript',
      'Designed to complement (not replace) licensed care teams',
    ],
    outcomes: 'Integrative Wellness Consultant · Health Coach · Behavior-Change Specialist',
  },
  {
    code: 'ONLINE_DOCTOR_VITAL_HEALTH_SCIENCES',
    courseId: 'doctor-vital-health-sciences',
    title: 'Doctor of Vital Health Sciences — Practitioner — Online',
    short: 'Capstone doctorate · integrative physiology · research literacy',
    duration: 'Self-Paced — Online',
    tuition: '$99',
    icon: Brain,
    accent: '#be185d',
    bg: '#fce7f3',
    badge: 'DOCTORATE',
    bio: 'Self-paced doctorate-level practitioner program in vital health sciences. Capstone-style program covering integrative physiology, nutrition science, lifestyle medicine, mind-body sciences, evidence-based research literacy, ethics & scope, and consultative practice. Prepares graduates to serve as non-licensed integrative-wellness practitioners.',
    features: [
      '4 reading modules · NCLEX-style quiz bank',
      '$99 enrollment · $99 mid-term · $199 final exam',
      'Doctorate certificate of completion + transcript',
      'Research-literacy focus: hierarchies of evidence, conflicts of interest',
    ],
    outcomes: 'Integrative Health Practitioner · Wellness Researcher · Capstone Doctorate',
  },
];

const LandingPage = () => {
  const [selectedProgram, setSelectedProgram] = useState(null);

  return (
    <div className="landing-page" data-testid="landing-page">
      <Seo
        title="MedCare Nursing Institute & Training Academy — DOL Apprenticeships, CMA, PN Tech, EMT & In-Home CNA Care · Memphis, TN"
        description="DOL-registered apprenticeship programs in CMA, Practical Nurse Tech, EMT, Phlebotomy, Medical Aide, GED and Homeland Security. Plus on-demand in-home Certified Nurse Assistant care at $25/hr with 24-hour notice."
        canonical="https://medcaretrainingacademy.com/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "MedCare Apprenticeship & Training Programs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Certified Medical Assistant (CMA) Training Program", "url": "https://medcaretrainingacademy.com/online-courses/cna-prep" },
            { "@type": "ListItem", "position": 2, "name": "Practical Nurse Tech Apprenticeship Program", "url": "https://medcaretrainingacademy.com/online-courses/lpn-prep" },
            { "@type": "ListItem", "position": 3, "name": "Emergency Medical Technician (EMT) Training", "url": "https://medcaretrainingacademy.com/online-courses/emt-prep" },
            { "@type": "ListItem", "position": 4, "name": "Phlebotomy Training", "url": "https://medcaretrainingacademy.com/online-courses/phlebotomy-prep" },
            { "@type": "ListItem", "position": 5, "name": "Master Homeland Security Specialist Training Program", "url": "https://medcaretrainingacademy.com/online-courses/homeland-security-prep" },
            { "@type": "ListItem", "position": 6, "name": "Hire a Certified Nurse Assistant — $25/hour", "url": "https://medcaretrainingacademy.com/hire-cna" }
          ]
        }}
      />
      {/* Government Header */}
      <div className="gov-header" data-testid="gov-header">
        <span className="gov-header-pill gold">U.S. Department of Labor · Registered Apprenticeship</span>
      </div>
      
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-logo">
          <img
            src={`${process.env.PUBLIC_URL || ''}/medcare_seal.png`}
            alt="MedCare Institutional Seal"
            data-testid="medcare-seal-logo"
            width="64"
            height="64"
            style={{ width: 64, height: 64, objectFit: 'contain', display: 'block', borderRadius: '50%' }}
          />
          <div className="nav-logo-text">
            <span className="nav-logo-title">MedCare Nursing Institute &amp; Training Academy</span>
            <span className="nav-logo-subtitle">Healthcare · Emergency Response · Technology Apprenticeships</span>
          </div>
        </div>
        <div className="nav-links">
          <Link to="/online-courses" className="nav-link" data-testid="online-courses-link" style={{ color: '#22c55e', fontWeight: '600' }}>Online Courses</Link>
          <Link to="/hire-cna" className="nav-link" data-testid="hire-cna-nav-link" style={{ color: '#16a34a', fontWeight: '600' }}>Hire a CNA</Link>
          <Link to="/pricing" className="nav-link" data-testid="pricing-nav-link" style={{ color: '#1e40af', fontWeight: '600' }}>Pricing</Link>
          <Link to="/association" className="nav-link" data-testid="association-nav-link" style={{ color: '#9333ea', fontWeight: 700 }}>Join Association</Link>
          <Link to="/employers" className="nav-link" data-testid="employers-link">Employers</Link>
          <Link to="/partner" className="nav-link" data-testid="partner-link">Partner With Us</Link>
          <Link to="/register" className="btn btn-success btn-sm" data-testid="apply-online-nav-btn" style={{ marginRight: '8px', background: '#22c55e' }}>
            Create Account
          </Link>
          <Link to="/login" className="btn btn-primary btn-sm" data-testid="login-btn">Student Portal</Link>
        </div>
      </nav>

      {/* Cinematic 3D programs cube — auto-rotates through all 6 program families.
          Replaces the legacy text-heavy hero per user request. */}
      <Programs3DCube />

      {/* Programs CTA — full catalog & tuition live on dedicated pages */}
      <section
        className="programs-section"
        id="programs"
        data-testid="programs-cta-section"
        style={{ textAlign: 'center', padding: '70px 24px' }}
      >
        <h2 className="section-title">Tuition &amp; Membership</h2>
        <p className="section-subtitle" style={{ maxWidth: 760, margin: '0 auto 36px' }}>
          Review tuition &amp; payment terms, or join the MedCare Association for
          ongoing professional benefits. The full course catalog is accessed
          from inside your Student Portal after enrollment.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 18,
          maxWidth: 1100,
          margin: '0 auto',
        }}>
          <Link
            to="/pricing"
            data-testid="cta-pricing"
            style={{
              background: 'linear-gradient(135deg,#1e40af,#1e3a8a)',
              color: '#ffffff',
              borderRadius: 14,
              padding: '28px 24px',
              textDecoration: 'none',
              boxShadow: '0 10px 24px -12px rgba(30,64,175,0.55)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <DollarSign size={26} />
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Tuition &amp; Pricing</div>
            <div style={{ fontSize: '0.88rem', opacity: 0.92 }}>
              $99 enrollment · $99 mid-term · $99 / $199 final
            </div>
            <div style={{ marginTop: 4, fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              View tuition terms <ArrowRight size={14} />
            </div>
          </Link>

          <Link
            to="/association"
            data-testid="cta-association"
            style={{
              background: 'linear-gradient(135deg,#9333ea,#6d28d9)',
              color: '#ffffff',
              borderRadius: 14,
              padding: '28px 24px',
              textDecoration: 'none',
              boxShadow: '0 10px 24px -12px rgba(147,51,234,0.55)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Award size={26} />
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Association Membership</div>
            <div style={{ fontSize: '0.88rem', opacity: 0.92 }}>
              MedCare Board of Nursing + Vital Health · $9.99/mo
            </div>
            <div style={{ marginTop: 4, fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Become a member <ArrowRight size={14} />
            </div>
          </Link>

          <Link
            to="/hire-cna"
            data-testid="cta-hire-cna"
            style={{
              background: 'linear-gradient(135deg,#16a34a,#15803d)',
              color: '#ffffff',
              borderRadius: 14,
              padding: '28px 24px',
              textDecoration: 'none',
              boxShadow: '0 10px 24px -12px rgba(22,163,74,0.55)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <HeartPulse size={26} />
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Hire a Certified Nurse Assistant</div>
            <div style={{ fontSize: '0.88rem', opacity: 0.92 }}>
              In-home CNA care · $25/hr · 4-hr min · 24-hr notice
            </div>
            <div style={{ marginTop: 4, fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Book a caregiver <ArrowRight size={14} />
            </div>
          </Link>
        </div>
      </section>
      
      {/* Workforce-development disclaimer */}

      {/* Footer */}
      <footer className="landing-footer" id="contact">
        <div className="footer-content">
          <div className="footer-logo">MedCare Nursing Institute &amp; Training Academy</div>
          <div className="footer-contact">
            <p>3150 E. Shelby Dr, Memphis TN 38118</p>
            <p>Phone: 901.625.1201</p>
            <p>Email: medcare.nursing@yahoo.com</p>
          </div>
          <div className="footer-badges">
            <span className="footer-badge">100% Online</span>
            <span className="footer-badge">U.S. Dept of Labor Registered</span>
            <span className="footer-badge">USDOL-Stamped Certificates</span>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/pricing" data-testid="footer-pricing-link" style={{ color: '#f7d774', fontSize: '0.85rem', textDecoration: 'underline' }}>
              View All Tuition &amp; Terms
            </Link>
            <Link to="/disclaimers" data-testid="footer-disclaimers-link" style={{ color: '#f7d774', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Disclaimers
            </Link>
            <Link to="/refund-policy" data-testid="footer-refund-link" style={{ color: '#f7d774', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Refund Policy
            </Link>
            <Link to="/association" data-testid="footer-association-link" style={{ color: '#f7d774', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Association · $9.99/mo
            </Link>
            <Link to="/verify/MC-XXX-XXXXX-YYYYMMDD" data-testid="footer-verify-link" style={{ color: '#f7d774', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Verify a Certificate
            </Link>
          </div>
          <p className="footer-copyright" data-testid="footer-copyright">
            © 2026 MedCare Nursing Institute &amp; Training Academy. All rights reserved.
            <br />
            A program of the <strong>MedCare National Board of Nursing &amp; Vital Health Sciences Association</strong> —
            3150 E. Shelby Dr, Memphis TN 38118 · 901.625.1201 · medcare.nursing@yahoo.com
          </p>
        </div>
      </footer>

      {/* Program Bio Modal */}
      {selectedProgram && (
        <div
          className="program-modal-backdrop"
          onClick={() => setSelectedProgram(null)}
          data-testid="program-modal-backdrop"
        >
          <div
            className="program-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="program-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedProgram.title} details`}
          >
            <button
              type="button"
              className="program-modal-close"
              onClick={() => setSelectedProgram(null)}
              data-testid="program-modal-close-btn"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div
              className="program-modal-header"
              style={{ background: selectedProgram.dark ? selectedProgram.bg : selectedProgram.accent }}
            >
              <div
                className="program-modal-icon"
                style={{ color: selectedProgram.dark ? '#f7d774' : '#ffffff' }}
              >
                <selectedProgram.icon size={34} />
              </div>
              <div>
                <span className="program-modal-badge">{selectedProgram.badge}</span>
                <h3>{selectedProgram.title}</h3>
                <div className="program-modal-meta">
                  <span><Clock size={14} /> {selectedProgram.duration}</span>
                  <span><DollarSign size={14} /> Tuition {selectedProgram.tuition}</span>
                </div>
              </div>
            </div>

            <div className="program-modal-body">
              <h4>About this program</h4>
              <p>{selectedProgram.bio}</p>

              <h4>What you'll learn</h4>
              <ul>
                {selectedProgram.features.map((f, i) => (
                  <li key={i}>
                    <CheckCircle size={14} /> <span>{f}</span>
                  </li>
                ))}
              </ul>

              <h4>Career outcomes</h4>
              <p className="program-modal-outcomes">{selectedProgram.outcomes}</p>

              <div className="program-modal-actions">
                <Link
                  to={`/register?program=${selectedProgram.code}`}
                  className="btn btn-primary"
                  data-testid={`program-modal-apply-${selectedProgram.code.toLowerCase()}`}
                  onClick={() => setSelectedProgram(null)}
                  style={{ flex: 1 }}
                >
                  Create Account &amp; Start Course — $99
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
